import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Activity, Building, Users, Upload, Bot, AlertTriangle,
  CheckCircle, XCircle, TrendingUp, RefreshCw, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PlatformMonitoring = () => {
  const [monitoring, setMonitoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    loadMonitoring();
  }, []);

  const loadMonitoring = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/monitoring`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMonitoring(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load monitoring:', error);
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const overview = monitoring?.overview || {};
  const uploads = monitoring?.uploads || {};
  const agents = monitoring?.agents || {};
  const usersByRole = monitoring?.usersByRole || {};
  const tenantActivity = monitoring?.recentTenantActivity || [];

  return (
    <div className="space-y-6" data-testid="platform-monitoring-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            <Activity className="inline h-8 w-8 mr-2 text-blue-600" />
            Platform Monitoring
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Real-time platform health and activity metrics
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {lastRefresh && (
            <span className="text-sm text-slate-500 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <Button onClick={loadMonitoring} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Companies</p>
                <p className="text-3xl font-bold text-blue-700">{overview.totalCompanies || 0}</p>
              </div>
              <Building className="h-10 w-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Companies</p>
                <p className="text-3xl font-bold text-green-700">{overview.activeCompanies || 0}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total Users</p>
                <p className="text-3xl font-bold text-purple-700">{overview.totalUsers || 0}</p>
              </div>
              <Users className="h-10 w-10 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/30 border-cyan-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-cyan-600">Total Uploads</p>
                <p className="text-3xl font-bold text-cyan-700">{uploads.total || 0}</p>
              </div>
              <Upload className="h-10 w-10 text-cyan-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Agent Runs</p>
                <p className="text-3xl font-bold text-orange-700">{agents.totalRuns || 0}</p>
              </div>
              <Bot className="h-10 w-10 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Health Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upload Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="h-5 w-5 mr-2 text-blue-600" />
              Upload Health
            </CardTitle>
            <CardDescription>Data ingestion success rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 dark:text-slate-400">Success Rate</span>
                <span className={`text-2xl font-bold ${
                  uploads.successRate >= 90 ? 'text-green-600' :
                  uploads.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {uploads.successRate || 100}%
                </span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full ${
                    uploads.successRate >= 90 ? 'bg-green-500' :
                    uploads.successRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${uploads.successRate || 100}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{(uploads.total || 0) - (uploads.failed || 0)}</p>
                  <p className="text-xs text-green-700">Successful</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{uploads.failed || 0}</p>
                  <p className="text-xs text-red-700">Failed</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agent Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Bot className="h-5 w-5 mr-2 text-purple-600" />
              Agent Health
            </CardTitle>
            <CardDescription>AI agent execution success rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 dark:text-slate-400">Success Rate</span>
                <span className={`text-2xl font-bold ${
                  agents.successRate >= 90 ? 'text-green-600' :
                  agents.successRate >= 70 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {agents.successRate || 100}%
                </span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full ${
                    agents.successRate >= 90 ? 'bg-green-500' :
                    agents.successRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${agents.successRate || 100}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{(agents.totalRuns || 0) - (agents.failedRuns || 0)}</p>
                  <p className="text-xs text-green-700">Successful</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{agents.failedRuns || 0}</p>
                  <p className="text-xs text-red-700">Failed</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users by Role & Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Users by Role */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-blue-600" />
              Users by Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(usersByRole).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                  <div className="flex items-center">
                    <span className={`w-3 h-3 rounded-full mr-3 ${
                      role === 'superadmin' ? 'bg-purple-500' :
                      role === 'admin' || role === 'company_admin' ? 'bg-blue-500' :
                      role === 'manager' ? 'bg-green-500' : 'bg-yellow-500'
                    }`}></span>
                    <span className="text-slate-700 dark:text-slate-300 capitalize">{role.replace('_', ' ')}</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">{count}</span>
                </div>
              ))}
              {Object.keys(usersByRole).length === 0 && (
                <p className="text-center text-slate-500 py-4">No user data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Tenant Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2 text-green-600" />
              Recent Tenant Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {tenantActivity.map((tenant, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{tenant.companyName}</p>
                    <p className="text-xs text-slate-500">
                      {tenant.lastActivity ? new Date(tenant.lastActivity).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{tenant.totalRuns}</p>
                    <p className="text-xs text-slate-500">runs</p>
                  </div>
                </div>
              ))}
              {tenantActivity.length === 0 && (
                <p className="text-center text-slate-500 py-4">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-4 h-4 rounded-full bg-green-500 animate-pulse"></div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">All Systems Operational</p>
                <p className="text-sm text-slate-500">Platform is running normally</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Generated at</p>
              <p className="font-mono text-sm text-slate-700 dark:text-slate-300">
                {monitoring?.generatedAt ? new Date(monitoring.generatedAt).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformMonitoring;
